import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectIsAuthenticated } from '../features/auth/authSlice';
import { selectUnreadCount } from '../features/notifications/notificationsSlice';
import { selectCreditBalance, selectEnforcementMode } from '../features/billing/billingSlice';
import { SIGNUP_BONUS_CREDITS, LOW_BALANCE_CREDITS } from '../features/billing/constants';
import { Home, Clock, FolderOpen, Bell, Coins } from 'lucide-react';
import { track } from '../analytics/analytics';
import './DashboardSidebar.css';

function DashboardSidebar({ isOpen, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const unreadCount = useSelector(selectUnreadCount);
  const creditBalance = useSelector(selectCreditBalance);
  const enforcementMode = useSelector(selectEnforcementMode);
  const currentPath = location.pathname;

  // Only surface credits once the backend actually charges for scans; while
  // enforcement is off, scans are free and a balance would just confuse.
  const showCredits = isAuthenticated && enforcementMode !== 'off' && creditBalance != null;
  const lowBalance = showCredits && creditBalance < LOW_BALANCE_CREDITS;

  const isActive = (path) => {
    return currentPath === path;
  };

  const handleNavClick = () => {
    // Close sidebar on mobile after navigation
    if (onClose) onClose();
  };

  const handleSignUp = () => {
    navigate('/login');
    if (onClose) onClose();
  };

  return (
    <aside className={`dashboard-sidebar ${isOpen ? 'open' : ''}`}>

      <nav className="sidebar-nav">
        {isAuthenticated ? (
          <>
            <Link
              to="/dashboard"
              className={`sidebar-nav-item ${isActive('/dashboard') ? 'active' : ''}`}
              onClick={handleNavClick}
            >
              <Home size={20} />
              <span>Dashboard</span>
            </Link>

            <Link
              to="/dashboard/videos1"
              className={`sidebar-nav-item ${isActive('/dashboard/videos1') || isActive('/dashboard/upload') ? 'active' : ''}`}
              onClick={handleNavClick}
            >
              <FolderOpen size={20} />
              <span>My Library</span>
            </Link>

            <Link
              to="/notifications"
              className={`sidebar-nav-item sidebar-mobile-notifications-item ${isActive('/notifications') ? 'active' : ''}`}
              onClick={handleNavClick}
            >
              <div className="sidebar-mobile-notifications-icon-wrap">
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="sidebar-mobile-notifications-dot" aria-hidden="true" />
                )}
              </div>
              <span>Notifications</span>
            </Link>
          </>
        ) : (
          <>
            <div className="sidebar-nav-item disabled">
              <Home size={20} />
              <span>Dashboard</span>
            </div>

            <div className="sidebar-nav-item disabled">
              <Clock size={20} />
              <span>History</span>
            </div>

            <div className="sidebar-nav-item disabled">
              <FolderOpen size={20} />
              <span>My Library</span>
            </div>

          </>
        )}
      </nav>

      {/* Credits box - signed-in users, once credits are actually charged */}
      {showCredits && (
        <div className={`sidebar-credits-box ${lowBalance ? 'low' : ''}`}>
          <div className="sidebar-credits-header">
            <Coins size={16} className="sidebar-credits-icon" aria-hidden="true" />
            <span className="sidebar-credits-label">Credits</span>
          </div>
          <div className="sidebar-credits-balance">{creditBalance.toLocaleString()}</div>
          {lowBalance && (
            <p className="sidebar-credits-warning">
              Running low — you may not be able to run your next scan.
            </p>
          )}
          <button
            className="sidebar-credits-button"
            onClick={() => { track('add_credits_clicked', { source: 'sidebar' }); navigate('/pricing'); if (onClose) onClose(); }}
          >
            Add credits
          </button>
        </div>
      )}

      {/* Promotional Box - Only for anonymous users */}
      {!isAuthenticated && (
        <div
          className="sidebar-promo-box"
          role="button"
          tabIndex={0}
          onClick={handleSignUp}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleSignUp();
            }
          }}
        >
          <h3 className="sidebar-promo-title">Unlock all features</h3>
          <p className="sidebar-promo-description">
            Create a free account for {SIGNUP_BONUS_CREDITS} free credits, saved videos,
            history, and multi-platform scans.
          </p>
          <button className="sidebar-promo-button">
            Sign up now
          </button>
        </div>
      )}
    </aside>
  );
}

export default DashboardSidebar;
